import { Field, useFormikContext } from 'formik';
import React, { FC, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocale } from '../../contexts/localeContext';
import { TranslationKeys } from '../../contexts/TranslationKeys';
import useFocusOnRouteChange from '../../hooks/useFocusOnRouteChange';
import { ListQuery } from '../../models/ListQuery';
import { IAppState } from '../../store/types';
import { validateIntegerWithinBounds, validateItemName, validateZeroAllowed } from '../../utilities/Validation';
import EntityHierarchy from '../entityHierarchy/entityHierarchy';
import Label from '../label/label';
import PaginatedSearchableSelectField from '../selection-field/paginated-searchable-select-field';
import { getBuildingNamesWithPagination } from '../../services/buildingService';
import { appendFilter } from '../../utilities/filters';

const FloorCard: FC = () => {
    const { translate } = useLocale();
    const { errors, values, setFieldValue } = useFormikContext<any>();
    const site = useSelector((state: IAppState) => state.site.site);
    const inputRef = useRef<HTMLInputElement>(null);
    useFocusOnRouteChange(inputRef);

    return <div className="w-full h-1/4 p-2 inline-flex items-start justify-start bg-white">
            {values.id
                ? <div className="ml-8 mr-5 py-4 card-details">
                <EntityHierarchy title={values.name} tree={{ label: values.site, node: { label: values.building, node: { label: values.name } } }} />
                </div> : null}

            <div className="flex flex-col">
                <div className="flex flex-col py-1">
                    <div className="flex flex-row justify-between" id="nameLabelId">
                        <Label htmlFor="name" className="text-xs text-gray-600 mt-1" required>{translate.byKey(TranslationKeys.name)}:</Label>
                        {errors.name ? <span className='text-red-600 text-xs'>{errors.name}</span> : null}
                    </div>
                    <Field
                        id="nameFieldId"
                        type="text"
                        innerRef={inputRef}
                        name="name"
                        className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-800 text-sm w-full bg-gray-input"
                        data-cy="nameField"
                        validate={(value: any) => validateItemName(value, translate)} />
                </div>

                <div className="flex flex-col py-1">
                    <div className="flex flex-row justify-between" id='siteLabelId'>
                        <Label htmlFor="site" className="text-xs text-gray-600 mt-1" required>{translate.byKey(TranslationKeys.site)}:</Label>
                    </div>
                    <Field
                        id="siteFieldId"
                        type="text"
                        name="site"
                        disabled
                        value={values.site || site.label || ''}
                        data-cy="siteField"
                        className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-500 text-sm w-full bg-gray-input cursor-not-allowed" />
                </div>
            </div>

            <div className="flex flex-col">
                <div className="flex flex-col px-4 py-1">
                    <div className="flex flex-row justify-between" id='buildingLabelId'>
                        <Label htmlFor="buildingId" className="text-xs text-gray-600 mt-1" required>{translate.byKey(TranslationKeys.building)}:</Label>
                        {errors.buildingId ? <span className='text-red-600 text-xs'>{errors.buildingId}</span> : null}
                    </div>
                    <PaginatedSearchableSelectField
                        name={translate.byKey(TranslationKeys.building)}
                        key="buildingId"
                        accessor='buildingId'
                        dataMethod={(listParameters?: ListQuery) => getBuildingNamesWithPagination({
                            ...listParameters,
                            filters: appendFilter(
                                values.id ? values.siteId : site.value,
                                'Site',
                                'site.id',
                                listParameters?.filters ?? []
                            )
                        })}
                        searchAccessor={'buildingName'}
                        required={true}
                        disabled={!!values.id}
                        onSelect={(_, item) => {
                            setFieldValue('building', item?.label)
                            setFieldValue('buildingId', item?.value)
                        }}
                        selectedItemLabel={values.building} 
                        dataCy="buildingField"/>
                </div>

                <div className="flex flex-col px-4 py-1">
                    <div className="flex flex-row justify-between" id="levelLabelId">
                        <Label htmlFor="level" className="text-xs text-gray-600 mt-1" required>{translate.byKey(TranslationKeys.level)}:</Label>
                        {errors.level ? <span className='text-red-600 text-xs'>{errors.level}</span> : null}
                    </div>
                    <Field
                        id="levelFieldId"
                        type="number"
                        name="level"
                        validate={(value: any) => validateZeroAllowed(value, -1000, undefined, translate)}
                        data-cy="levelField"
                        className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-800 text-sm w-full bg-gray-input" />
                </div>
            </div>
        </div>
};

export default FloorCard;
